import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useState } from 'react'
import { testsAPI } from '../api/client'
import {
  PlayCircle, CheckCircle, XCircle, Clock, AlertCircle,
  Film, Image, FileText, Download, Eye, RotateCw, ChevronDown, ChevronUp,
  Trash2, X, ExternalLink
} from 'lucide-react'

// Форматирование длительности
function formatDuration(ms) {
  if (!ms) return '-'
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return minutes > 0
    ? `${minutes}м ${remainingSeconds}с`
    : `${remainingSeconds}с`
}

// Форматирование времени
function formatTimeAgo(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffDay > 0) return `${diffDay}д назад`
  if (diffHour > 0) return `${diffHour}ч назад`
  if (diffMin > 0) return `${diffMin}м назад`
  return 'только что'
}

// Статус бейдж
function StatusBadge({ status }) {
  const configs = {
    pending: { icon: Clock, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50', label: 'Ожидает' },
    running: { icon: RotateCw, color: 'bg-blue-500/20 text-blue-400 border-blue-500/50', label: 'Идёт' },
    passed: { icon: CheckCircle, color: 'bg-green-500/20 text-green-400 border-green-500/50', label: 'Пройден' },
    failed: { icon: XCircle, color: 'bg-red-500/20 text-red-400 border-red-500/50', label: 'Провален' },
    error: { icon: AlertCircle, color: 'bg-red-500/20 text-red-400 border-red-500/50', label: 'Ошибка' },
  }

  const config = configs[status] || configs.pending
  const Icon = config.icon

  return (
    <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 border ${config.color}`}>
      <Icon size={14} className={status === 'running' ? 'animate-spin' : ''} />
      {config.label}
    </span>
  )
}

// Карточка теста
function TestSuiteCard({ suite, onRun, isRunning }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="glass p-5 rounded-xl">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white">{suite.title}</h3>
          <p className="text-sm text-white/60 mt-1">{suite.group_name}</p>
          {suite.description && (
            <p className="text-sm text-white/40 mt-2">{suite.description}</p>
          )}
        </div>
        <button
          onClick={() => onRun(suite.file_name)}
          disabled={isRunning}
          className="btn-primary flex items-center gap-2 ml-4 disabled:opacity-50"
        >
          <PlayCircle size={16} className={isRunning ? 'animate-spin' : ''} />
          Запустить
        </button>
      </div>

      {suite.tests && suite.tests.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Скрыть' : 'Показать'} тесты ({suite.tests.length})
          </button>

          {expanded && (
            <ul className="mt-3 space-y-2">
              {suite.tests.map((test, idx) => (
                <li key={idx} className="text-sm text-white/60 pl-4 border-l-2 border-white/10">
                  • {test.name}
                  {test.description && (
                    <span className="text-white/40 ml-2">— {test.description}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// История запусков
function TestRunsHistory({ runs, onSelectRun, selectedRunId, onClearOld }) {
  if (!runs || runs.length === 0) {
    return (
      <div className="glass p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">История запусков</h3>
        <p className="text-white/40">Запусков пока не было. Нажмите «Запустить все тесты».</p>
      </div>
    )
  }

  return (
    <div className="glass p-6 rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Последние запуски</h3>
        <button
          onClick={onClearOld}
          className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
        >
          <Trash2 size={14} />
          Очистить старые
        </button>
      </div>
      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {runs.map(run => (
          <div
            key={run.id}
            onClick={() => onSelectRun(run)}
            className={`p-4 rounded-lg cursor-pointer transition-colors ${
              selectedRunId === run.id
                ? 'bg-blue-500/20 border border-blue-500/50'
                : 'bg-white/5 hover:bg-white/10'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <StatusBadge status={run.status} />
              <span className="text-sm text-white/60">
                {formatTimeAgo(run.started_at)}
              </span>
            </div>

            <div className="flex items-center gap-4 text-sm text-white/60">
              <span>Всего: {run.total_tests}</span>
              <span className="text-green-400">Успешно: {run.passed_tests}</span>
              {run.failed_tests > 0 && (
                <span className="text-red-400">Ошибки: {run.failed_tests}</span>
              )}
              <span className="ml-auto">{formatDuration(run.duration_ms)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Извлечение тестов из test_results
function extractTests(testResults) {
  if (!testResults || !Array.isArray(testResults)) return []

  const tests = []

  function traverse(suites) {
    for (const suite of suites) {
      if (suite.specs) {
        for (const spec of suite.specs) {
          for (const test of spec.tests || []) {
            const result = test.results?.[0]
            tests.push({
              title: spec.title,
              suite: suite.title,
              status: result?.status || 'unknown',
              duration: result?.duration,
              error: result?.error?.message || result?.errors?.[0]?.message,
            })
          }
        }
      }
      if (suite.suites) {
        traverse(suite.suites)
      }
    }
  }

  traverse(testResults)
  return tests
}

// Детали запуска с артефактами
function TestRunDetails({ run, artifacts, onShowReport }) {
  const [showTests, setShowTests] = useState(true)

  if (!run) {
    return (
      <div className="glass p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Детали запуска</h3>
        <p className="text-white/40">Выберите запуск, чтобы посмотреть детали</p>
      </div>
    )
  }

  const tests = extractTests(run.test_results)
  const failedTests = tests.filter(t => t.status === 'failed' || t.status === 'timedOut')
  const passedTests = tests.filter(t => t.status === 'passed')

  return (
    <div className="glass p-6 rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Запуск #{run.id}</h3>
        <div className="flex items-center gap-3">
          {run.test_results && (
            <button
              onClick={() => onShowReport(run)}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              <ExternalLink size={14} />
              Полный отчёт
            </button>
          )}
          <StatusBadge status={run.status} />
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-white/5 rounded-lg">
          <div className="text-2xl font-bold text-white">{run.total_tests}</div>
          <div className="text-sm text-white/60">Всего</div>
        </div>
        <div className="text-center p-3 bg-green-500/10 rounded-lg">
          <div className="text-2xl font-bold text-green-400">{run.passed_tests}</div>
          <div className="text-sm text-green-400/60">Успешно</div>
        </div>
        <div className="text-center p-3 bg-red-500/10 rounded-lg">
          <div className="text-2xl font-bold text-red-400">{run.failed_tests}</div>
          <div className="text-sm text-red-400/60">Ошибки</div>
        </div>
        <div className="text-center p-3 bg-white/5 rounded-lg">
          <div className="text-2xl font-bold text-white">{formatDuration(run.duration_ms)}</div>
          <div className="text-sm text-white/60">Длительность</div>
        </div>
      </div>

      {/* Список тестов */}
      {tests.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowTests(!showTests)}
            className="text-md font-semibold text-white mb-3 flex items-center gap-2"
          >
            {showTests ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Результаты тестов ({tests.length})
          </button>

          {showTests && (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {/* Failed tests first */}
              {failedTests.map((test, idx) => (
                <div key={`failed-${idx}`} className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle size={14} className="text-red-400" />
                    <span className="text-white font-medium text-sm">{test.title}</span>
                    <span className="text-white/40 text-xs ml-auto">{formatDuration(test.duration)}</span>
                  </div>
                  <div className="text-xs text-white/50 mb-1">{test.suite}</div>
                  {test.error && (
                    <pre className="text-xs text-red-300/80 mt-2 p-2 bg-black/30 rounded overflow-x-auto whitespace-pre-wrap">
                      {test.error.slice(0, 500)}
                    </pre>
                  )}
                </div>
              ))}

              {/* Passed tests */}
              {passedTests.map((test, idx) => (
                <div key={`passed-${idx}`} className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    <span className="text-white/80 text-sm">{test.title}</span>
                    <span className="text-white/40 text-xs ml-auto">{formatDuration(test.duration)}</span>
                  </div>
                  <div className="text-xs text-white/40">{test.suite}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Артефакты */}
      {artifacts && artifacts.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-white mb-3">Артефакты</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {artifacts.map(artifact => (
              <div key={artifact.id} className="p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  {artifact.artifact_type === 'video' && <Film className="text-blue-400" size={20} />}
                  {artifact.artifact_type === 'screenshot' && <Image className="text-purple-400" size={20} />}
                  {artifact.artifact_type === 'trace' && <FileText className="text-green-400" size={20} />}

                  <span className="text-white font-medium">{artifact.artifact_type}</span>
                  {artifact.is_failure && (
                    <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">ошибка</span>
                  )}
                </div>

                <p className="text-sm text-white/60 mb-2 truncate">{artifact.test_name}</p>

                <div className="flex gap-2">
                  <a
                    href={`/api/tests/artifacts/${artifact.id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <Eye size={14} />
                    Открыть
                  </a>
                  <a
                    href={`/api/tests/artifacts/${artifact.id}/download`}
                    className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1"
                  >
                    <Download size={14} />
                    Скачать
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Логи ошибок */}
      {run.stderr && run.status !== 'passed' && (
        <div>
          <h4 className="text-md font-semibold text-white mb-3">Вывод ошибки</h4>
          <pre className="p-4 bg-black/40 rounded-lg text-sm text-white/80 overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap">
            {run.stderr}
          </pre>
        </div>
      )}
    </div>
  )
}

// Модалка с полным отчётом
function ReportModal({ run, onClose }) {
  if (!run) return null

  const tests = extractTests(run.test_results)

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold text-white">Отчёт по тестам - запуск #{run.id}</h2>
            <p className="text-white/60 text-sm mt-1">
              Успешно: {run.passed_tests}, ошибок: {run.failed_tests} • {formatDuration(run.duration_ms)}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {tests.map((test, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border ${
                  test.status === 'passed'
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  {test.status === 'passed'
                    ? <CheckCircle className="text-green-400 mt-0.5" size={18} />
                    : <XCircle className="text-red-400 mt-0.5" size={18} />
                  }
                  <div className="flex-1">
                    <div className="font-medium text-white">{test.title}</div>
                    <div className="text-sm text-white/50">{test.suite}</div>
                    {test.error && (
                      <pre className="mt-3 p-3 bg-black/40 rounded text-sm text-red-300/80 overflow-x-auto whitespace-pre-wrap">
                        {test.error}
                      </pre>
                    )}
                  </div>
                  <span className="text-white/40 text-sm">{formatDuration(test.duration)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TestDashboard() {
  const [selectedRun, setSelectedRun] = useState(null)
  const [reportRun, setReportRun] = useState(null)
  const queryClient = useQueryClient()

  // Fetch suites
  const { data: suites } = useQuery('testSuites', testsAPI.getSuites, {
    retry: false,
    onError: () => {} // Suppress errors for missing suites
  })

  // Fetch runs
  const { data: runs, isLoading: runsLoading } = useQuery(
    'testRuns',
    () => testsAPI.getRuns({ limit: 20 }),
    {
      refetchInterval: 5000  // Обновляем каждые 5 секунд для отслеживания running тестов
    }
  )

  // Fetch stats
  const { data: stats } = useQuery('testStats', testsAPI.getStats)

  // Fetch artifacts for selected run
  const { data: artifacts } = useQuery(
    ['testArtifacts', selectedRun?.id],
    () => testsAPI.getArtifacts(selectedRun.id),
    { enabled: !!selectedRun }
  )

  // Mutation для запуска тестов
  const runTestsMutation = useMutation(testsAPI.runTests, {
    onSuccess: () => {
      queryClient.invalidateQueries('testRuns')
      queryClient.invalidateQueries('testStats')
    }
  })

  // Mutation для очистки старых
  const clearOldMutation = useMutation(testsAPI.clearOldRuns, {
    onSuccess: () => {
      queryClient.invalidateQueries('testRuns')
      queryClient.invalidateQueries('testStats')
      setSelectedRun(null)
    }
  })

  const handleRunTests = (testFile = null) => {
    runTestsMutation.mutate({
      project: 'chromium',
      test_file: testFile,
      triggered_by: 'dashboard'
    })
  }

  const handleClearOld = () => {
    if (confirm('Удалить все запуски старше 24 часов?')) {
      clearOldMutation.mutate({ hours: 24 })
    }
  }

  // Проверяем есть ли running тесты
  const hasRunningTests = runs?.some(r => r.status === 'running' || r.status === 'pending')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <PlayCircle className="w-7 h-7 text-blue-400" />
            Тестовый стенд
          </h1>
          <p className="text-white/60 mt-1">
            E2E-тесты Playwright для AGR Mini App
          </p>
        </div>

        <button
          onClick={() => handleRunTests()}
          disabled={runTestsMutation.isLoading || hasRunningTests}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <PlayCircle size={16} className={(runTestsMutation.isLoading || hasRunningTests) ? 'animate-spin' : ''} />
          Запустить все тесты
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="glass p-5 rounded-xl">
            <div className="text-3xl font-bold text-white">{stats.total_runs}</div>
            <div className="text-white/60">Всего запусков</div>
          </div>
          <div className="glass p-5 rounded-xl">
            <div className="text-3xl font-bold text-green-400">
              {stats.success_rate.toFixed(1)}%
            </div>
            <div className="text-white/60">Успешность</div>
          </div>
          <div className="glass p-5 rounded-xl">
            <div className="text-3xl font-bold text-blue-400">
              {stats.latest_run ? formatTimeAgo(stats.latest_run.started_at) : '-'}
            </div>
            <div className="text-white/60">Последний запуск</div>
          </div>
        </div>
      )}

      {/* Test Suites */}
      {suites && suites.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Наборы тестов</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {suites.map(suite => (
              <TestSuiteCard
                key={suite.id}
                suite={suite}
                onRun={handleRunTests}
                isRunning={runTestsMutation.isLoading || hasRunningTests}
              />
            ))}
          </div>
        </div>
      )}

      {/* Default suite when no suites in DB */}
      {(!suites || suites.length === 0) && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Доступные тесты</h2>
          <div className="glass p-5 rounded-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">AGR E2E-тесты</h3>
                <p className="text-sm text-white/60 mt-1">wall.spec.js</p>
                <p className="text-sm text-white/40 mt-2">
                  Галерея, авторизация, страница стены, API mock и проверка элементов интерфейса
                </p>
              </div>
              <button
                onClick={() => handleRunTests()}
                disabled={runTestsMutation.isLoading || hasRunningTests}
                className="btn-primary flex items-center gap-2 ml-4 disabled:opacity-50"
              >
                <PlayCircle size={16} className={(runTestsMutation.isLoading || hasRunningTests) ? 'animate-spin' : ''} />
                Запустить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TestRunsHistory
          runs={runs}
          onSelectRun={setSelectedRun}
          selectedRunId={selectedRun?.id}
          onClearOld={handleClearOld}
        />

        <TestRunDetails
          run={selectedRun}
          artifacts={artifacts}
          onShowReport={setReportRun}
        />
      </div>

      {/* Report Modal */}
      {reportRun && (
        <ReportModal run={reportRun} onClose={() => setReportRun(null)} />
      )}
    </div>
  )
}
